import { push } from 'react-router-redux';
import isEmpty from 'lodash/isEmpty';
import {
    fetchEvents as fetchEventsApi,
    fetchAllEvents as fetchAllEventsApi,
    fetchAllOrganizers as fetchAllOrganizersApi,
    fetchUpcomingEvent as fetchUpcomingEventApi,
    fetchStatusCounts as fetchStatusCountsApi,
    fetchEventsByIds as fetchEventsByIdsApi,
    fetchCheckoutSettings as fetchCheckoutSettingsApi,
} from './api';
import {
    DEFAULT_EVENT_EXPANSIONS,
    DEFAULT_EVENT_PAGE_SIZE,
    DEFAULT_PAGE_NUMBER,
    DEFAULT_SEARCH_QUERY,
    DEFAULT_ORGANIZER_ID,
    EVENT_VENUE_EXPANSION,
    ORDER_BY_START_ASC,
    ORDER_BY_START_DESC,
    STATUS_FILTER_ALL,
    STATUS_FILTER_DRAFT_AND_PUBLISHED,
    STATUS_FILTER_DRAFT,
    STATUS_FILTER_FUTURE,
    STATUS_FILTER_PAST,
    STATUS_FILTER_PUBLISHED,
} from './constants';
import { serializeEventParamsToUrl, transformEvents } from './utils';

export const ADD_EVENTS = 'ADD_EVENTS';
export const ADD_EVENTS_SUGGESTIONS = 'ADD_EVENTS_SUGGESTIONS';
export const UPDATE_EVENTS = 'UPDATE_EVENTS';
export const ADD_ORGANIZERS = 'ADD_ORGANIZERS';
export const GET_STATUS_COUNTS = 'GET_STATUS_COUNTS';

export const UPDATE_NEW_EVENT_STATUS_FEATURE_VALUE =
    'UPDATE_NEW_EVENT_STATUS_FEATURE_VALUE';

export const GET_CHECKOUT_SETTINGS = 'GET_CHECKOUT_SETTINGS';

export const fetchCheckoutSettings = (payload) => ({
    type: GET_CHECKOUT_SETTINGS,
    payload,
});

export const updateEvents = (payload) => ({
    type: UPDATE_EVENTS,
    payload,
});

const addEvents = (payload) => ({
    type: ADD_EVENTS,
    payload,
});

const addEventsSuggestions = (payload) => ({
    type: ADD_EVENTS_SUGGESTIONS,
    payload,
});

const addOrganizers = (payload) => ({
    type: ADD_ORGANIZERS,
    payload,
});

const getStatusCounts = (payload) => ({
    type: GET_STATUS_COUNTS,
    payload,
});

// This is duplicated in django/src/www/eventbrite/ebapps/organizations/utils.py
const STATUS_ROUTE_MAP = {
    [STATUS_FILTER_ALL]: {
        status: 'all',
    },
    [STATUS_FILTER_PUBLISHED]: {
        status: 'live,started,ended,canceled',
        time_filter: 'current_future',
    },
    [STATUS_FILTER_DRAFT]: {
        status: 'draft',
    },
    [STATUS_FILTER_PAST]: {
        status: 'live,started,ended,canceled',
        time_filter: 'past',
    },
    [STATUS_FILTER_FUTURE]: {
        time_filter: 'current_future',
    },
    [STATUS_FILTER_DRAFT_AND_PUBLISHED]: {
        status: 'draft,live,started,ended',
        time_filter: 'current_future',
    },
};

const STATUS_ORDER_MAP = {
    [STATUS_FILTER_ALL]: ORDER_BY_START_ASC,
    [STATUS_FILTER_PUBLISHED]: ORDER_BY_START_ASC,
    [STATUS_FILTER_DRAFT]: ORDER_BY_START_ASC,
    [STATUS_FILTER_PAST]: ORDER_BY_START_DESC,
    [STATUS_FILTER_FUTURE]: ORDER_BY_START_ASC,
    [STATUS_FILTER_DRAFT_AND_PUBLISHED]: ORDER_BY_START_ASC,
};

/**
 *
 * @param {EventParams} filterParams
 */
const _getFetchEventsParams = ({
    status = STATUS_FILTER_PUBLISHED,
    searchQuery = DEFAULT_SEARCH_QUERY,
    pageNumber = DEFAULT_PAGE_NUMBER,
    pageSize = DEFAULT_EVENT_PAGE_SIZE,
    organizerId = DEFAULT_ORGANIZER_ID,
}) => ({
    // NOTE: This is duplicated in ebapps/organizations/utils.py (_get_event_soa_params)
    order_by: STATUS_ORDER_MAP[status],
    name_filter: searchQuery,
    page: pageNumber,
    page_size: pageSize,
    organizer_filter:
        organizerId === DEFAULT_ORGANIZER_ID ? undefined : organizerId,
    ...STATUS_ROUTE_MAP[status],
});

/**
 * Fetches upcoming event for any series of repeating events and mutates the event object to include upcomingEvent
 * @param {object} eventData
 * @param {string[]} eventData.eventIds - event ids of the events within an organization
 * @param {object} eventData.eventEntities - normalized event entities of the events within an organization
 */
const fetchUpcomingEvents = ({ eventIds, eventEntities }) => {
    const seriesEvents = eventIds.filter(
        (eventId) => eventEntities[eventId].isSeries,
    );
    const eventsWithUpcomingEventPromises = seriesEvents.map((eventId) =>
        fetchUpcomingEventApi(eventEntities[eventId].seriesId),
    );

    return Promise.all(eventsWithUpcomingEventPromises)
        .then((responses) => {
            // eslint-disable-next-line array-callback-return, consistent-return
            const parentEvents = responses.map((response) => {
                const {
                    eventIds: upcomingEventIds,
                    eventEntities: upcomingEventEntities,
                } = response;

                if (!isEmpty(upcomingEventEntities)) {
                    const upcomingEvent =
                        upcomingEventEntities[upcomingEventIds[0]];
                    const parentEvent = eventEntities[upcomingEvent.seriesId];

                    parentEvent.upcomingEvent = upcomingEvent;

                    return parentEvent;
                }
            });

            return transformEvents(parentEvents).eventEntities;
        })
        .then((seriesParentEventEntities) => ({
            eventIds,
            eventEntities: {
                ...eventEntities,
                ...seriesParentEventEntities,
            },
        }));
};

/**
 * @typedef {'all' | 'published' | 'draft' | 'past' | 'future'} Status
 *
 *
 * @typedef {object} EventParams
 * @property {Status} [status] Event status filter
 * @property {string} [searchQuery] query string
 * @property {string|number} [pageNumber] current pagination page number
 * @property {number} [pageSize] current pagination page size
 * @property {string} [organizerId] current organizer (profile) ID
 */

/**
 * Fetches events for an organization and dispatches a `ADD_EVENTS` action with the normalized data
 * @param {string} organizationId - ID of the organization
 * @param {EventParams} [filterParams={}] - Filter/sort parameters
 * @return {Function} - redux-thunk handler
 */
export const fetchEvents = (organizationId, filterParams = {}) => (dispatch) =>
    fetchEventsApi(organizationId, {
        show_series_parent: true,
        expand: DEFAULT_EVENT_EXPANSIONS,
        ..._getFetchEventsParams(filterParams),
    })
        .then(({ normalizedEvents, pagination }) =>
            Promise.all([pagination, fetchUpcomingEvents(normalizedEvents)]),
        )
        .then(([pagination, updatedNormalizedEvents]) => ({
            normalizedEvents: updatedNormalizedEvents,
            pagination,
        }))
        .then(
            ({ normalizedEvents, pagination }) => {
                dispatch(
                    addEvents({
                        organizationId,
                        params: filterParams,
                        total: pagination['object_count'],
                        ...normalizedEvents,
                    }),
                );

                return normalizedEvents;
            },
            () => {
                dispatch(
                    addEvents({
                        organizationId,
                        params: filterParams,
                        total: 0,
                        eventEntities: {},
                        eventIds: [],
                    }),
                );

                throw Error('Events request failed');
            },
        );

export const fetchEventsSuggestions = (organizationId, params = {}) => (
    dispatch,
) =>
    fetchEventsApi(organizationId, {
        show_series_parent: true,
        expand: EVENT_VENUE_EXPANSION,
        ..._getFetchEventsParams(params),
    }).then(({ normalizedEvents, pagination }) => {
        dispatch(
            addEventsSuggestions({
                organizationId,
                params,
                total: pagination['object_count'],
                ...normalizedEvents,
            }),
        );
        return normalizedEvents;
    });

export const fetchAllEvents = (organizationId) => (dispatch) =>
    fetchAllEventsApi(organizationId, {
        show_series_parent: true,
        expand: DEFAULT_EVENT_EXPANSIONS,
    }).then((events) => {
        dispatch(
            addEvents({
                organizationId,
                ...events,
            }),
        );
        return events;
    });

export const fetchEventsByIds = (organizationId, eventIds) => (dispatch) =>
    fetchEventsByIdsApi(eventIds, {
        show_series_parent: true,
        expand: DEFAULT_EVENT_EXPANSIONS,
    }).then((events) => {
        dispatch(
            addEvents({
                organizationId,
                ...events,
            }),
        );
        return events;
    });

/**
 * Filters the events indirectly by redirecting to events URL w/ updated parameters
 * @param {string} eventsBasePath URL to base events page
 * @param {EventParams} [filterParams={}] Filter params to serialize
 * @return {import('react-router-redux').RouterAction} - redux-thunk handler
 */
export const filterEvents = (eventsBasePath, filterParams = {}) =>
    // Redirect to events URL w/ updated parameters
    push(serializeEventParamsToUrl(eventsBasePath, filterParams));

/**
 * Fetches organizers for an organization and dispatches a `ADD_ORGANIZERS` action with the normalized data
 * @param {string} organizationId - ID of the organization
 * @return {Function} - redux-thunk handler
 */
export const fetchAllOrganizers = (organizationId) => (dispatch) =>
    fetchAllOrganizersApi(organizationId).then(
        ({ organizerEntities, organizerIds }) =>
            dispatch(
                addOrganizers({
                    organizationId,
                    organizerEntities,
                    organizerIds,

                    // NOTE: for now there are no filter params, but in the future
                    // if we update fetchAllOrganizers to have filters/sorts, we
                    // can pass them through
                    params: {},
                }),
            ),
    );

/**
 * Fetches events status counts and dispatches a `GET_STATUS_COUNTS` action with the normalized data
 * @param {string} organizationId - ID of the organization
 * @return {Function} - redux-thunk handler
 */
export const fetchStatusCounts = (organizationId) => (dispatch) =>
    fetchStatusCountsApi(organizationId).then(({ eventCount }) =>
        dispatch(getStatusCounts({ eventCount })),
    );

export const getCheckoutSettings = (organizationId) => (dispatch, getState) => {
    if (isEmpty(getState().checkoutSettings)) {
        return fetchCheckoutSettingsApi(
            organizationId,
        ).then(({ checkoutSettings }) =>
            dispatch(fetchCheckoutSettings(checkoutSettings)),
        );
    }
    return Promise.resolve();
};
